import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FileText } from "lucide-react";

const KINDS = [
  { k: "runs",      label: "Runs",     desc: "Every workflow run with status, cost, timing." },
  { k: "articles",  label: "Articles", desc: "Published + drafts with slug, cost, uniqueness." },
  { k: "costs",     label: "Cost Ledger", desc: "Unified ledger — LLM + DFS + Text.ru + Images." },
  { k: "timeline",  label: "Timeline stages", desc: "One row per stage across all runs." },
  { k: "errors",    label: "Errors",   desc: "Classified errors (Provider/Budget/Network/etc)." },
  { k: "budget",    label: "Budget",   desc: "Current limits + hard-limit setting." },
  { k: "config",    label: "Configuration snapshot", desc: "Full ops-config export." },
  { k: "health",    label: "Health events", desc: "Last 24h of service events." },
];
const FORMATS = ["json", "csv"];

export default function Reports() {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const [days, setDays] = React.useState(30);

  function href(kind, fmt) {
    const token = localStorage.getItem("seo:jwt") || "";
    // Note: /reports/* is auth-guarded; direct href download works because
    // seoApi keeps JWT in cookies too. If cookie strategy changes, switch to
    // programmatic fetch with Authorization header + Blob download.
    return `${backend}/api/seo/ops/reports/${kind}?fmt=${fmt}&days=${days}&_t=${token.slice(0,10)}`;
  }

  async function download(kind, fmt) {
    const token = localStorage.getItem("seo:jwt") || "";
    const r = await fetch(`${backend}/api/seo/ops/reports/${kind}?fmt=${fmt}&days=${days}`,
                            { headers: { Authorization: `JWT ${token}` } });
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = r.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1]
                  || `seo-${kind}-${days}d.${fmt}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="p-6" data-testid="reports-page">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <FileText size={22} /> Reports
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Export any operational dataset as JSON or CSV.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-neutral-500">Window:</span>
          <select className="border rounded px-2 py-1.5" value={days} onChange={(e) => setDays(+e.target.value)}
                    data-testid="reports-days">
            <option value={1}>1 day</option>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {KINDS.map((it) => (
          <Card key={it.k} data-testid={`report-${it.k}`}>
            <CardHeader>
              <CardTitle className="text-base">{it.label}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-neutral-600">
              <p className="mb-3">{it.desc}</p>
              <div className="flex gap-2">
                {FORMATS.map((fmt) => (
                  <Button key={fmt} variant="outline" size="sm"
                            onClick={() => download(it.k, fmt)}
                            data-testid={`btn-${it.k}-${fmt}`}
                            className="gap-1.5">
                    <Download size={13} /> {fmt.toUpperCase()}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <p className="text-xs text-neutral-500 mt-6">
        PDF and Excel exports, plus scheduled (daily/weekly/monthly) reports, are on the Phase 3 backlog.
      </p>
    </div>
  );
}
